import styled from 'styled-components';
import { GlobalToken } from 'antd';

export const CardContainer = styled.div<{
  $gradient: string;
  $fixedHeight?: boolean;
  $disabled?: boolean;
  theme: { token: GlobalToken };
}>`
  display: flex;
  flex-direction: column;
  background: ${({ $gradient }) => $gradient};
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  padding: 0;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  ${({ $fixedHeight }) => ($fixedHeight ? 'height: 270px;' : 'min-height: 270px;')}
  overflow: hidden;
  position: relative;
  width: 100%;
  transform: translateZ(0);
  will-change: transform;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:hover {
    ${({ $disabled }) =>
      !$disabled &&
      `
      transform: translateY(-2px) translateZ(0);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    `}
  }
`;

export const CardContentWrapper = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  padding: ${({ theme: { token } }) => token.paddingLG}px ${({ theme: { token } }) => token.paddingLG}px
    ${({ theme: { token } }) => token.marginXS}px ${({ theme: { token } }) => token.paddingLG}px;
  gap: ${({ theme: { token } }) => token.marginLG}px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

export const CardHeader = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  min-height: 29px;
`;

export const ValueChip = styled.div<{ theme: { token: GlobalToken } }>`
  background: ${({ theme: { token } }) => token.colorFillQuaternary};
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  padding: ${({ theme: { token } }) => token.paddingXXS}px ${({ theme: { token } }) => token.padding}px;
  display: inline-flex;
  align-items: center;
  height: 24px;
`;

export const ValueText = styled.span<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextLightSolid};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
`;

export const CardContent = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { token } }) => token.margin}px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

export const CardTitle = styled.h4<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextBase};
  font-size: ${({ theme: { token } }) => token.fontSizeHeading4 || 20}px;
  font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
  margin: 0;
`;

export const CardMeta = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.margin}px;
  padding-right: ${({ theme: { token } }) => token.paddingXL}px;
`;

export const DeviceInfo = styled.span<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextBase};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export const Dot = styled.span<{ theme: { token: GlobalToken } }>`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${({ theme: { token } }) => token.colorTextBase};
  flex-shrink: 0;
`;

export const DateInfo = styled.span<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorText};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const CareIconWrapper = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  height: 20px;
`;

export const CardDescription = styled.p<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextSecondary};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 24px;
  height: 48px;
`;

export const CardFooter = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  padding: 0;
  margin-bottom: ${({ theme: { token } }) => token.marginLG}px;
  min-height: 40px;
  position: relative;
  flex-shrink: 0;
`;

export const TypeIndicator = styled.div<{ $color: string; theme: { token: GlobalToken } }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
  border-radius: ${({ theme: { token } }) => token.borderRadiusSM}px;
`;

export const FooterContent = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  padding-left: ${({ theme: { token } }) => token.paddingLG}px;
  padding-right: ${({ theme: { token } }) => token.paddingLG}px;
  gap: ${({ theme: { token } }) => token.marginSM}px;
  min-width: 0;
  overflow: hidden;
`;

export const TypeLabel = styled.span<{ $color: string; theme: { token: GlobalToken } }>`
  color: ${({ $color }) => $color};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  font-weight: 400;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  line-height: 1;
`;

export const ArrowIconButton = styled.button<{ theme: { token: GlobalToken } }>`
  background: transparent;
  border: none;
  padding: ${({ theme: { token } }) => token.paddingXS}px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme: { token } }) => token.colorText};
  transition: all 0.3s ease;
  border-radius: ${({ theme: { token } }) => token.borderRadiusSM}px;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover:not(:disabled) {
    background: ${({ theme: { token } }) => token.colorFillTertiary};
    color: ${({ theme: { token } }) => token.colorPrimary};
  }

  &:active:not(:disabled) {
    background: ${({ theme: { token } }) => token.colorFillSecondary};
  }

  &:disabled {
    cursor: not-allowed;
  }

  .anticon {
    font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
  }
`;

export const ActionButtonsContainer = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginXXS / 2}px;
  flex-shrink: 0;
  flex-grow: 0;
  min-width: fit-content;
`;

export const ActionButton = styled.button<{ theme: { token: GlobalToken } }>`
  background: transparent;
  border: none;
  padding: ${({ theme: { token } }) => token.paddingXXS}px ${({ theme: { token } }) => token.paddingSM}px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme: { token } }) => token.colorPrimary};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  font-weight: 400;
  transition: all 0.3s ease;
  border-radius: ${({ theme: { token } }) => token.borderRadiusSM}px;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover:not(:disabled) {
    background: ${({ theme: { token } }) => token.colorFillTertiary};
  }

  &:active:not(:disabled) {
    background: ${({ theme: { token } }) => token.colorFillSecondary};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const ThreeDotsButton = styled.button<{ theme: { token: GlobalToken } }>`
  background: transparent;
  border: 1px solid ${({ theme: { token } }) => token.colorPrimary};
  border-radius: ${({ theme: { token } }) => token.borderRadiusSM}px;
  padding: ${({ theme: { token } }) => token.paddingXXS}px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
  width: 24px;
  height: 24px;

  &:hover {
    background: ${({ theme: { token } }) => token.colorFillTertiary};
  }

  &:active {
    background: ${({ theme: { token } }) => token.colorFillSecondary};
  }
`;
